<template>
  <v-container fluid class="pa-6 pt-3">
    <v-card class="mb-5" elevation="0">
      <v-card-text
        class="pa-3"
        style="padding-left: 0px !important; padding-bottom: 0px !important"
      >
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model.trim="form.usn_id"
              label="USN"
              outlined
              dense
              clearable
              hide-details="auto"
              @keyup.enter="search"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model.trim="form.application_id"
              label="Application ID"
              outlined
              dense
              clearable
              hide-details="auto"
              @keyup.enter="search"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="form.fee_types"
              :items="feeTypes"
              item-text="text"
              item-value="id"
              label="Fee Type"
              outlined
              dense
              clearable
              multiple
              chips
              small-chips
              deletable-chips
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="form.term_no"
              :items="termOptions"
              label="Payment Term"
              outlined
              dense
              clearable
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="displayDate"
                  label="Refund Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="resetDates"
                />
              </template>
              <v-date-picker
                v-model="selectedDates"
                range
                @input="onDateChange"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="form.source_type"
              :items="sourceOptions"
              item-text="text"
              item-value="value"
              label="Source"
              outlined
              dense
              clearable
              multiple
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="form.status"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              label="Status"
              outlined
              dense
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <v-btn
              class="accent white--text"
              :loading="loading"
              :disabled="loading"
              @click="search"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Search
            </v-btn>

            <v-btn
              class="accent white--text ml-2"
              :loading="downloadLoading"
              :disabled="downloadLoading || !refunds.length"
              @click="downloadRefunds"
            >
              <v-icon left small>mdi-download</v-icon>
              Download
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row dense class="mb-2">
      <v-col v-for="tile in summaryTiles" :key="tile.label" cols="6" md="3">
        <v-sheet outlined rounded class="pa-3 text-center">
          <div class="caption grey--text text--darken-1">{{ tile.label }}</div>
          <div class="subtitle-1 font-weight-bold" :class="tile.class">
            {{ tile.value }}
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-sheet outlined rounded>
      <v-data-table
        :headers="refundedListHeaders"
        :items="refunds"
        :loading="loading"
        item-key="id"
        fixed-header
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
      >
        <template v-slot:no-data>
          <div class="text-center py-4 grey--text">No refunds raised yet</div>
        </template>

        <template #[`item.refunded_date`]="{ item }">
          {{ formatDate(item.refunded_date) }}
        </template>

        <template #[`item.student_name`]="{ item }">
          {{ item.student_name || "—" }}
        </template>

        <template #[`item.source_label`]="{ item }">
          <v-chip x-small outlined color="primary">
            {{ item.source_label }}
          </v-chip>
        </template>

        <template #[`item.fee_type`]="{ item }">
          {{ item.fee_type || "—" }}
        </template>

        <template #[`item.usn_id`]="{ item }">
          {{ item.usn_id || "—" }}
        </template>

        <template #[`item.application_id`]="{ item }">
          {{ item.application_id || "—" }}
        </template>

        <template #[`item.term_no`]="{ item }">
          {{ item.term_no || "—" }}
        </template>

        <template #[`item.paid_amount`]="{ item }">
          {{ money(item.paid_amount) }}
        </template>

        <template #[`item.amount`]="{ item }">
          <span class="orange--text text--darken-2 font-weight-bold">
            {{ money(item.amount) }}
          </span>
        </template>

        <template #[`item.payment_mode`]="{ item }">
          {{ item.payment_mode || "—" }}
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip
            small
            dark
            class="mt-1"
            :color="item.status === 'cancelled' ? 'grey' : 'green darken-1'"
          >
            {{ item.status === "cancelled" ? "Cancelled" : "Active" }}
          </v-chip>
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-end" style="gap: 6px">
            <v-btn icon small color="primary" @click="openDetails(item)">
              <v-icon small>mdi-eye-outline</v-icon>
            </v-btn>
            <v-btn
              x-small
              outlined
              color="red darken-1"
              class="pt-1"
              :loading="cancellingId === item.id"
              :disabled="item.status === 'cancelled'"
              @click="openCancelDialog(item)"
            >
              Cancel
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-sheet>

    <v-dialog v-model="detailsDialog" max-width="720" scrollable>
      <v-card v-if="detailsDialog">
        <v-card-title class="font-weight-medium secondary--text primary">
          Refund Details
          <v-spacer />
          <v-btn small icon @click="detailsDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <div class="caption grey--text text-uppercase mb-2">Payment</div>
          <v-row dense class="mb-3">
            <v-col
              v-for="field in paymentFields"
              :key="field.label"
              cols="6"
              md="4"
            >
              <div class="caption grey--text">{{ field.label }}</div>
              <div class="body-2">{{ field.value || "—" }}</div>
            </v-col>
          </v-row>

          <v-divider class="mb-3" />

          <div class="caption grey--text text-uppercase mb-2">Refund</div>
          <v-row dense>
            <v-col
              v-for="field in refundFields"
              :key="field.label"
              cols="6"
              md="4"
            >
              <div class="caption grey--text">{{ field.label }}</div>
              <div class="body-2">{{ field.value || "—" }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="480" persistent>
      <v-card v-if="cancelDialog">
        <v-card-title class="font-weight-medium secondary--text primary">
          Cancel Refund
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="body-2 mb-3">
            Cancelling releases the payment so it can be refunded again. This is
            recorded against the refund.
          </p>
          <v-text-field
            v-model.trim="cancellationRemarks"
            label="Reason"
            outlined
            dense
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text @click="cancelDialog = false">Close</v-btn>
          <v-btn
            color="red darken-1"
            dark
            :loading="cancellingId === (selectedRefund && selectedRefund.id)"
            @click="confirmCancel"
          >
            Cancel Refund
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { refundedListHeaders } from "@/constants/headers";

export default {
  data() {
    return {
      refundedListHeaders,
      loading: false,
      downloadLoading: false,
      dateMenu: false,
      selectedDates: [],
      refunds: [],
      detailsDialog: false,
      cancelDialog: false,
      selectedRefund: null,
      cancellationRemarks: "",
      cancellingId: null,
      form: {
        usn_id: "",
        application_id: "",
        source_type: [],
        fee_types: [],
        term_no: null,
        status: "active",
        from_date: "",
        to_date: "",
      },
      feeTypes: [
        { id: "Term Fee", text: "Term Fee" },
        { id: "Annual", text: "Annual" },
        { id: "Full", text: "Full" },
        { id: "Advance Tuition Fee", text: "Advance Tuition Fee" },
        { id: "Exam Fee", text: "Exam Fee" },
        { id: "Re-Admission Fee", text: "Re-Admission Fee" },
        { id: "Convocation Fee", text: "Convocation Fee" },
        { id: "Postal Fee", text: "Postal Fee" },
        { id: "Add-on Programme", text: "Add-on Programme" },
      ],
      sourceOptions: [
        { text: "Student Invoice", value: "student_invoice" },
        { text: "Admit Plan", value: "student_admit_plan_payment" },
        { text: "Pre-Application", value: "pre_application_fee_payment" },
      ],
      termOptions: [1, 2, 3, 4, 5, 6],
      statusOptions: [
        { text: "Active", value: "active" },
        { text: "Cancelled", value: "cancelled" },
      ],
    };
  },

  computed: {
    displayDate() {
      if (!this.form.from_date) return "";
      if (!this.form.to_date || this.form.to_date === this.form.from_date) {
        return this.form.from_date;
      }
      return `${this.form.from_date} → ${this.form.to_date}`;
    },

    summaryTiles() {
      const refunded = this.refunds
        .filter((r) => r.status !== "cancelled")
        .reduce((total, r) => total + this.toNumber(r.amount), 0);

      const students = new Set(
        this.refunds.map((r) => r.usn_id || r.application_id),
      ).size;

      return [
        { label: "Refunds", value: this.refunds.length },
        { label: "Students", value: students },
        {
          label: "Amount Refunded",
          value: this.money(refunded),
          class: "orange--text text--darken-2",
        },
        {
          label: "Cancelled",
          value: this.refunds.filter((r) => r.status === "cancelled").length,
          class: "grey--text",
        },
      ];
    },

    paymentFields() {
      const r = this.selectedRefund || {};
      return [
        { label: "Student", value: r.student_name },
        { label: "USN", value: r.usn_id },
        { label: "Application ID", value: r.application_id },
        { label: "Source", value: r.source_label },
        { label: "Fee Type", value: r.fee_type },
        { label: "Payment Term", value: r.term_no },
        { label: "Amount Paid", value: this.money(r.paid_amount) },
        { label: "Transaction ID", value: r.payment_transaction_id },
        { label: "Paid On", value: this.formatDate(r.payment_date) },
      ];
    },

    refundFields() {
      const r = this.selectedRefund || {};
      return [
        { label: "Refund Amount", value: this.money(r.amount) },
        { label: "Refund Txn ID", value: r.refund_transaction_id },
        { label: "Refund Date", value: this.formatDate(r.refunded_date) },
        { label: "Bank", value: r.bank_name },
        { label: "Account Number", value: r.account_number },
        { label: "IFSC", value: r.ifsc_code },
        { label: "Mode", value: r.payment_mode },
        { label: "Remarks", value: r.remarks },
        {
          label: "Status",
          value: r.status === "cancelled" ? "Cancelled" : "Active",
        },
        { label: "Cancellation Reason", value: r.cancellation_remarks },
      ];
    },
  },

  created() {
    const usn = this.$route.query.usn_id;
    if (usn) this.form.usn_id = usn;

    this.$store.commit("setBreadcrumbs", [
      { text: "Refunds", to: { name: "Refunds" } },
      { text: "Refund List" },
    ]);

    this.fetchRefunds();
  },

  beforeDestroy() {
    this.$store.commit("removePageSubtitle2");
  },

  methods: {
    toNumber(value) {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    },

    money(value) {
      return `₹${this.toNumber(value).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      })}`;
    },

    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    onDateChange() {
      if (!this.selectedDates.length) return;
      const [start, end] = [...this.selectedDates].sort();
      this.form.from_date = start;
      this.form.to_date = end || start;
      if (this.selectedDates.length === 2) this.dateMenu = false;
    },

    resetDates() {
      this.selectedDates = [];
      this.form.from_date = "";
      this.form.to_date = "";
    },

    search() {
      this.fetchRefunds();
    },

    buildQuery() {
      const params = {
        ...this.form,
        fee_types: this.form.fee_types.join(","),
        source_type: this.form.source_type.join(","),
      };

      return Object.keys(params)
        .filter(
          (key) =>
            params[key] !== undefined &&
            params[key] !== null &&
            params[key] !== "",
        )
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`,
        )
        .join("&");
    },

    async fetchRefunds() {
      this.loading = true;
      try {
        const res = await this.$axios.get(
          `/payment_refunds?${this.buildQuery()}`,
        );
        this.refunds = res.data.data || [];
      } catch (e) {
        console.log(e);
        this.$store.commit("showSnackbar", {
          message: "Failed to load refunds",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    // Downloads the same rows the filters are showing, not just this page.
    async downloadRefunds() {
      this.downloadLoading = true;
      try {
        const res = await this.$axios.get(
          `/download_payment_refunds?${this.buildQuery()}`,
          { responseType: "blob" },
        );
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Refunds.csv");
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (e) {
        console.log(e);
        this.$store.commit("showSnackbar", {
          message: "Failed to download refunds",
          color: "error",
        });
      } finally {
        this.downloadLoading = false;
      }
    },

    openDetails(refund) {
      this.selectedRefund = refund;
      this.detailsDialog = true;
    },

    openCancelDialog(refund) {
      this.selectedRefund = refund;
      this.cancellationRemarks = "";
      this.cancelDialog = true;
    },

    async confirmCancel() {
      this.cancellingId = this.selectedRefund.id;
      try {
        await this.$axios.put(
          `/payment_refunds/${this.selectedRefund.id}/cancel`,
          { cancellation_remarks: this.cancellationRemarks || null },
        );
        this.$store.commit("showSnackbar", {
          message: "Refund cancelled",
          color: "success",
        });
        this.cancelDialog = false;
        this.fetchRefunds();
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Failed to cancel refund";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.cancellingId = null;
      }
    },
  },
};
</script>
